import Moment from 'moment';
import { extendMoment } from 'moment-range';

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const moment = extendMoment(Moment as any);

/**
 * Normalizes highlighted dates to YYYY-MM-DD format
 */
export const normalizeHighlightedDates = (highlightedDates: Array<string | Date>): Set<string> => {
  return new Set(
    highlightedDates
      .map((date) => {
        try {
          const dateObj = typeof date === 'string' ? new Date(date) : date;
          return moment(dateObj).startOf('day').format('YYYY-MM-DD');
        } catch (error) {
          console.warn('Error normalizing addition date:', date, error);
          return null;
        }
      })
      .filter((date): date is string => date !== null),
  );
};

/**
 * Marks calendar days with addition indicators based on highlighted dates
 */
export const markAdditionDays = (normalizedAdditionDates: Set<string>): void => {
  // First, clear all previous highlights
  const allDateElements = document.querySelectorAll('.DateRangePicker__Date');
  allDateElements.forEach((element) => {
    element.classList.remove('DateRangePicker__DateHighlighted');
  });

  // Find all month containers
  const monthElements = document.querySelectorAll('.DateRangePicker__Month');
  monthElements.forEach((monthElement) => {
    const monthHeader = monthElement.querySelector('.DateRangePicker__MonthHeader');
    if (!monthHeader) return;

    const monthText = monthHeader.textContent?.trim();
    if (!monthText) return;

    // Parse month header - try different formats
    // Format could be "NOV 2025", "November 2025", etc.
    let monthIndex = -1;
    let year = -1;

    // Try format like "NOV 2025" or "NOVEMBER 2025"
    const monthMatch = monthText.match(/(\w+)\s+(\d{4})/);
    if (monthMatch) {
      const monthName = monthMatch[1];
      year = parseInt(monthMatch[2], 10);
      // Try to parse month name
      const testMoment = moment(`${monthName} 1, ${year}`, ['MMM D, YYYY', 'MMMM D, YYYY'], true);
      if (testMoment.isValid()) {
        monthIndex = testMoment.month();
      } else {
        // Fallback: try moment's month parsing
        const parsed = moment().month(monthName);
        if (parsed.isValid()) {
          monthIndex = parsed.month();
        }
      }
    }

    if (monthIndex === -1 || year === -1) return;

    // Track which day numbers have been processed in this month to prevent duplicate highlighting
    const processedDayNumbers = new Set<number>();

    // Find all date cells in this month
    const dateElements = monthElement.querySelectorAll('.DateRangePicker__Date');
    dateElements.forEach((element) => {
      const dateLabel = element.querySelector('.DateRangePicker__DateLabel');
      if (!dateLabel) return;

      const dayNumber = parseInt(dateLabel.textContent || '', 10);
      if (isNaN(dayNumber) || dayNumber < 1 || dayNumber > 31) return;

      // Skip if this day number has already been processed in this month
      if (processedDayNumbers.has(dayNumber)) return;

      // Create the full date
      const cellDate = moment([year, monthIndex, dayNumber]);
      if (!cellDate.isValid()) return;

      // Check if this date has an addition
      const cellDateString = cellDate.format('YYYY-MM-DD');
      const isRangeEdge =
        element.classList.contains('DateRangePicker__Date--range-start') ||
        element.classList.contains('DateRangePicker__Date--range-end');

      // Mark this day number as processed (even if it's a range edge, to prevent highlighting overflow days)
      processedDayNumbers.add(dayNumber);

      // Only highlight if this date matches an addition date and is not a range edge
      if (normalizedAdditionDates.has(cellDateString) && !isRangeEdge) {
        // Add class to highlight the date text (only if not range start/end)
        // We allow highlighting for dates in the middle of a selected range
        element.classList.add('DateRangePicker__DateHighlighted');
      }
    });
  });
};

/**
 * Sets up automatic highlighting of dates in the calendar
 * Returns a cleanup function to stop the highlighting
 */
export const setupDateHighlighting = (highlightedDates: Array<string | Date>): (() => void) | undefined => {
  const normalizedAdditionDates = normalizeHighlightedDates(highlightedDates);

  if (highlightedDates.length === 0) {
    const allDateElements = document.querySelectorAll('.DateRangePicker__Date');
    allDateElements.forEach((element) => {
      element.classList.remove('DateRangePicker__DateHighlighted');
    });
    return undefined;
  }

  setTimeout(() => {
    markAdditionDays(normalizedAdditionDates);
  }, 0);

  // Watch for calendar changes (month navigation, etc.)
  const observer = new MutationObserver(() => {
    markAdditionDays(normalizedAdditionDates);
  });

  // Observe the calendar container and all its children
  const calendarContainer = document.querySelector('.DateRangePicker');
  if (calendarContainer) {
    observer.observe(calendarContainer, {
      childList: true,
      subtree: true, // Watch entire subtree to catch all changes
    });
  }

  // Listen for range classes being applied
  const handleRangeClassesApplied = () => {
    markAdditionDays(normalizedAdditionDates);
  };
  document.addEventListener('rangeClassesApplied', handleRangeClassesApplied);

  // Return cleanup function
  return () => {
    observer.disconnect();
    document.removeEventListener('rangeClassesApplied', handleRangeClassesApplied);
  };
};
